import styled from '@emotion/styled';
import { useCallback, useEffect, useState } from 'react';
import { useTestContext } from '../../context/testContext';
import { useAppointment } from '../../context/appointmentContext';
import { usePostMessage } from '../../context/messageContext';
import { client } from '../../helpers/api-client';
import { examDisplayName } from '../optician/exams/examCatalog';
import { sessionApi, appointmentPath, isNotAvailable } from '../../helpers/sessionApi';
import { DataTable, TableAction, ArrowRightIcon, TrashIcon, SessionActions, useSessionUI, colors, fonts } from '../../ui';

// C6 "Completed Examinations" (SPEC 4.6): Test Name | Date | View Results -> | Delete Results.
// Data: GET /api/appointments/<id>/examinations/ (BE-7); falls back to appointment.tests_results
// (legacy) while the endpoint is missing. Delete: DELETE .../examinations/<result_id>/ (legacy:
// DELETE api/appointment-test-result/<id>/).

// test_key -> clinician results tab (same mapping as the legacy completed-test-results screen).
export const RESULTS_TAB_BY_KEY = {
  crowdedLogmar: 'acuityResults',
  openLogmar: 'acuityResults',
  randomE: 'acuityResults',
  kaysOptotypes: 'acuityResults',
  kaysMultiple: 'acuityResults',
  optimaChart: 'icnResults',
  contrastSensitivity: 'contrastSensitivityResults',
  visualFieldTwo: 'visualFieldResults',
  visualFieldForthValley: 'neuroSuiteResults',
  RefractionTwo: 'refractionResults',
  refractionTwo: 'refractionResults',
  ishihara: 'colourVisionResults',
};

const pad = (n) => String(n).padStart(2, '0');
export const formatExamDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value); // legacy submission_date is preformatted
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Normalise both API shapes to one row shape.
export const toExamRow = (r) => ({
  id: r.id,
  testKey: r.test_key,
  // Same display names as the Examinations picker ("Open logMAR", "Colour Vision", "Near Vision").
  name: examDisplayName({ key: r.test_key, title: r.test_name || r.test_title || r.test_key }),
  version: r.version || null,
  date: r.submitted_at || r.submission_date || r.created_at,
  summary: r.summary || '',
  clinician: typeof r.clinician === 'object' ? r.clinician?.name : r.clinician,
});

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  font-family: ${fonts.family};
`;

const Name = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  small {
    color: ${colors.textMuted};
    font-size: 11px;
  }
`;

const Notice = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${colors.textMuted};
  text-align: center;
`;

const CompletedExaminations = ({ setActiveTab, setTitle }) => {
  const { updateTestData } = useTestContext();
  const { appointment } = useAppointment();
  const postMessage = usePostMessage();
  const { confirm, toast } = useSessionUI();
  const appointmentId = appointment?.currentAppointment?.id;
  const [rows, setRows] = useState(null);
  const [source, setSource] = useState('examinations'); // examinations | legacy
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!appointmentId) return;
    setError(null);
    try {
      const data = await sessionApi(appointmentPath(appointmentId, 'examinations/'));
      setRows((Array.isArray(data) ? data : data?.results || []).map(toExamRow));
      setSource('examinations');
    } catch (e) {
      if (!isNotAvailable(e)) {
        setError(e);
        setRows([]);
        return;
      }
      try {
        const { tests_results } = await client(`api/appointments/${appointmentId}/`);
        setRows((tests_results || []).filter((t) => !t.async_test).map(toExamRow));
        setSource('legacy');
      } catch (e2) {
        setError(e2);
        setRows([]);
      }
    }
  }, [appointmentId]);

  useEffect(() => {
    setTitle?.('Completed Examinations');
    load();
    updateTestData({ testLeftEye: false, testRightEye: false, leftEye: null, rightEye: null, bothEyes: null, testResultsId: null });
  }, [setTitle, load, updateTestData]);

  const view = (row) => {
    const tab = RESULTS_TAB_BY_KEY[row.testKey];
    if (!tab) {
      toast({ tone: 'info', title: 'No results view', body: `Results for "${row.name}" can be seen in the dashboard.` });
      return;
    }
    updateTestData({ testResultsId: row.id });
    setTitle?.(`${row.name} - Results`);
    setActiveTab(tab);
  };

  const remove = async (row) => {
    const ok = await confirm({
      message: `Delete the ${row.name} results?`,
      detail: 'The results will be removed from this appointment. This cannot be undone.',
      yesLabel: 'Delete',
      noLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      if (source === 'examinations') {
        try {
          await sessionApi(appointmentPath(appointmentId, `examinations/${row.id}/`), { method: 'DELETE' });
        } catch (e) {
          if (!isNotAvailable(e)) throw e;
          await sessionApi(`api/appointment-test-result/${row.id}/`, { method: 'DELETE' });
        }
      } else {
        await sessionApi(`api/appointment-test-result/${row.id}/`, { method: 'DELETE' });
      }
      setRows((list) => list.filter((r) => r.id !== row.id));
      toast({ tone: 'success', title: 'Results deleted', body: `${row.name} was removed from this appointment.` });
    } catch (e) {
      toast({ tone: 'error', title: 'Could not delete the results', body: e.message });
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Test Name',
      render: (r) => (
        <Name>
          {r.version && !String(r.name).endsWith(r.version) ? `${r.name} ${r.version}` : r.name}
          {r.summary && <small>{r.summary}</small>}
        </Name>
      ),
    },
    { key: 'date', label: 'Date', render: (r) => formatExamDate(r.date) },
    {
      key: 'view',
      label: 'View Results',
      width: '18%',
      render: (r) => (
        <TableAction aria-label={`View ${r.name} results`} onClick={() => view(r)}>
          <ArrowRightIcon size={20} />
        </TableAction>
      ),
    },
    {
      key: 'delete',
      label: 'Delete Results',
      width: '18%',
      render: (r) => (
        <TableAction tone='danger' aria-label={`Delete ${r.name} results`} onClick={() => remove(r)}>
          <TrashIcon size={20} />
        </TableAction>
      ),
    },
  ];

  return (
    <Wrap data-testid='completed-examinations'>
      <DataTable
        columns={columns}
        rows={rows || []}
        caption='Completed examinations'
        empty={rows === null ? 'Loading…' : error ? `Examinations could not be loaded: ${error.message}` : 'No tests have been completed in this session yet.'}
      />
      {source === 'legacy' && rows?.length > 0 && <Notice>Showing results from the appointment record.</Notice>}
      <SessionActions
        onBack={() => {
          postMessage({ type: 'testNavigationReset' });
          updateTestData({ testKey: null });
          setActiveTab('test');
        }}
        primary={{
          label: 'Consultation View',
          onClick: () => {
            postMessage({ type: 'consultationView' });
            postMessage({ type: 'endOfTest' });
            setActiveTab('consultationView');
          },
        }}
      />
    </Wrap>
  );
};

export default CompletedExaminations;
